import React, { useState, useEffect, useCallback } from 'react';
import { Fingerprint, CheckCircle, AlertCircle, Loader2, CreditCard } from 'lucide-react';
import './KycVerification.css';

const KYC_STATUS = {
    NOT_STARTED: 'NOT_STARTED',
    INITIATING: 'INITIATING',
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETED: 'COMPLETED',
    FAILED: 'FAILED'
};

const KycVerification = ({ userId, onComplete, environment = 'sandbox' }) => {
    const [status, setStatus] = useState(KYC_STATUS.NOT_STARTED);
    const [error, setError] = useState(null);
    const [digioLoaded, setDigioLoaded] = useState(false);

    const API_URL = process.env.REACT_APP_API_URL || '';

    // Inject Digio SDK
    useEffect(() => {
        const scriptId = 'digio-sdk-script';
        if (document.getElementById(scriptId)) {
            setDigioLoaded(true);
            return;
        }

        const script = document.createElement('script');
        script.id = scriptId;
        script.src = 'https://app.digio.in/sdk/v11/digio.js';
        script.async = true;
        script.onload = () => {
            console.log('✅ Digio SDK Loaded');
            setDigioLoaded(true);
        };
        script.onerror = () => {
            console.error('❌ Failed to load Digio SDK');
            setError('Failed to load verification interface. Please try again later.');
            setStatus(KYC_STATUS.FAILED);
        };
        document.head.appendChild(script);

        return () => {
            // Optional: Clean up script if needed, but usually better to keep it cached
        };
    }, []);

    const updateStatusOnBackend = useCallback(async (currentStatus, kid, digioResponse = null) => {
        try {
            const session = JSON.parse(localStorage.getItem('userSession'));
            const response = await fetch(`${API_URL}/api/kyc/update-status`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session?.token || ''}`
                },
                body: JSON.stringify({
                    userId,
                    status: currentStatus,
                    kid,
                    response: digioResponse
                })
            });
            const data = await response.json();
            if (!data.success) {
                console.warn('Backend update failed:', data.message);
            }
        } catch (err) {
            console.error('Error updating status on backend:', err);
        }
    }, [API_URL, userId]);

    const startKyc = async () => {
        if (!digioLoaded) {
            setError('Verification system is still loading. Please wait.');
            return;
        }

        setError(null);
        setStatus(KYC_STATUS.INITIATING);

        try {
            const session = JSON.parse(localStorage.getItem('userSession'));
            // 1. Call backend to initiate KYC and get KID
            const response = await fetch(`${API_URL}/api/kyc/initiate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session?.token || ''}`
                },
                body: JSON.stringify({ userId })
            });

            const data = await response.json();

            if (!data.success) {
                throw new Error(data.message || 'Failed to initiate verification');
            }

            const { kid, email, accessToken, environment: digioEnv } = data;

            // 2. Initialize Digio SDK
            const options = {
                environment: digioEnv || environment,
                callback: function (digioResponse) {
                    if (digioResponse.error_code) {
                        console.error('Digio Error:', digioResponse.error_code);
                        setStatus(KYC_STATUS.FAILED);
                        setError(`Verification failed: ${digioResponse.message || digioResponse.error_code}`);
                        updateStatusOnBackend('FAILED', kid, digioResponse);
                    } else {
                        console.log('Digio Success:', digioResponse);
                        setStatus(KYC_STATUS.COMPLETED);
                        updateStatusOnBackend('COMPLETED', kid, digioResponse);
                        if (onComplete) onComplete(digioResponse);
                    }
                },
                logo: "https://your-brand-logo-url.com/logo.png" // Optional
            };

            const digio = new window.Digio(options);

            setStatus(KYC_STATUS.IN_PROGRESS);
            digio.submit(kid, email, accessToken);

        } catch (err) {
            console.error('KYC Initiation Error:', err);
            setError(err.message || 'Something went wrong. Please try again.');
            setStatus(KYC_STATUS.FAILED);
        }
    };

    const renderContent = () => {
        switch (status) {
            case KYC_STATUS.COMPLETED:
                return (
                    <>
                        <div className="success-checkmark">
                            <CheckCircle size={40} />
                        </div>
                        <h3 className="kyc-title">Identity Verified</h3>
                        <p className="kyc-description">
                            Great news! Your identity has been successfully verified.
                            You can now proceed with your loan applications.
                        </p>
                        <div className="kyc-status-badge status-completed">
                            ✓ VERIFIED
                        </div>
                    </>
                );

            case KYC_STATUS.INITIATING:
            case KYC_STATUS.IN_PROGRESS:
                return (
                    <>
                        <div className="kyc-icon-wrapper">
                            <Loader2 size={32} className="animate-spin" />
                        </div>
                        <h3 className="kyc-title">Verification in Progress</h3>
                        <p className="kyc-description">
                            {status === KYC_STATUS.INITIATING
                                ? 'Preparing your secure verification session...'
                                : 'Please complete the steps in the Digio window.'}
                        </p>
                        <div className={`kyc-status-badge status-${status.toLowerCase()}`}>
                            {status}
                        </div>
                    </>
                );

            case KYC_STATUS.FAILED:
                return (
                    <>
                        <div className="kyc-icon-wrapper" style={{ background: '#fef2f2', color: '#dc2626' }}>
                            <AlertCircle size={32} />
                        </div>
                        <h3 className="kyc-title">Verification Failed</h3>
                        <p className="kyc-description">
                            {error || 'We couldn\'t verify your identity. Please try again or contact support.'}
                        </p>
                        <button className="kyc-button" onClick={startKyc}>
                            Try Again
                        </button>
                    </>
                );

            default:
                return (
                    <>
                        <div className="kyc-icon-wrapper">
                            <Fingerprint size={32} />
                        </div>
                        <h3 className="kyc-title">Verify Your Identity</h3>
                        <p className="kyc-description">
                            To ensure the security of your account and comply with regulations,
                            we need to verify your identity using Digio.
                        </p>
                        <button className="kyc-button" onClick={startKyc} disabled={!digioLoaded}>
                            {!digioLoaded ? (
                                <>
                                    <Loader2 size={18} className="animate-spin" />
                                    Loading SDK...
                                </>
                            ) : (
                                <>
                                    <CreditCard size={18} />
                                    Start Verification
                                </>
                            )}
                        </button>
                    </>
                );
        }
    };

    return (
        <div className="kyc-card">
            {renderContent()}
            <style>{`
                .animate-spin {
                    animation: kyc-spin 1s linear infinite;
                }
            `}</style>
        </div>
    );
};

export default KycVerification;
